import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { EntryInfoFragment } from '@/api/entries/query.generated';
import { ErrorIcon } from '@/assets/icons/error';
import { Box } from '@/components/lib/components';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import BetrAnalytics from '@/feature/analytics/analytics';
import { CxtType, addCxtTag } from '@/feature/analytics/connextraTag';
import { AnalyticsEvent, EVENT_TYPES, RUDDERSTACK_EVENTS } from '@/feature/analytics/constants';
import { trackRudderStackEvent } from '@/feature/analytics/trackRudderStackEvent';
import { P2pMaxEntryAmountExceeded } from '@/feature/entries-pickem/components/P2pMaxEntryAmountExceeded';
import { markLinkConversion } from '@/feature/entry-share/utils/social-link';
import { openLocationSettings } from '@/feature/permissions';
import { userLimitsKeys } from '@/feature/responsible-gaming/hooks/query-keys';
import { CreateEntryError } from '@/hooks/use-entries';
import { useWalletStore } from '@/hooks/use-wallet';
import { userPromosKeys } from '@/providers';
import { Currency, GameMode } from '@/types/api.generated';
import { getErrorInfo } from '@/utils/errors/entry-errors';
import { logger } from '@/utils/logging';
import { useQueryClient } from '@tanstack/react-query';

import { hasInvalidProjections } from '../utils/betslip-utils';
import { useBetslipStore } from './use-betslip-store';

type UseEntryResultHandlersOptions = {
    onInvalidProjectionsFound?: () => void;
    isMysteryMode?: boolean;
};

export const usePlaceEntryHandlers = ({
    onInvalidProjectionsFound,
    isMysteryMode = false,
}: UseEntryResultHandlersOptions = {}) => {
    const { showInfoSheet } = useAlerts();
    const { t } = useTranslation(['p2p', 'betslip_pickem']);
    const queryClient = useQueryClient();
    const loadUserWallet = useWalletStore(state => state.actions.loadUserWallet);

    const activeGameMode = useBetslipStore(state => state.activeGameMode);
    const setSelectedPromo = useBetslipStore(state => state.actions.setSelectedPromo);
    const resetAutoPromoApplied = useBetslipStore(state => state.actions.resetAutoPromoApplied);
    const setEntryAmount = useBetslipStore(state => state.actions.setEntryAmount);
    const setCurrency = useBetslipStore(state => state.actions.setCurrency);
    const setQuickAmountsMode = useBetslipStore(state => state.actions.setQuickAmountsMode);
    const handleSuccess = useCallback(
        async (newEntry: EntryInfoFragment) => {
            BetrAnalytics.trackEntryPlaced(newEntry.amount, newEntry.id);
            trackRudderStackEvent(RUDDERSTACK_EVENTS.submit('Entry Successful'), {
                amount: newEntry.amount,
                eventType: EVENT_TYPES.ENTRY,
            });
            addCxtTag({
                pageType: CxtType.BET_CONFIRM,
                includeAccountId: true,
                value: newEntry.amount.toString(),
                currency: 'USD',
            });
            BetrAnalytics.trackEvent(AnalyticsEvent.PICKS_ENTRY_SUCCESS, {
                gameMode: newEntry.gameMode,
            });

            if (!isMysteryMode) {
                setSelectedPromo(undefined);
                resetAutoPromoApplied();
            }

            if (newEntry.gameMode !== GameMode.RaceToPlace) {
                setEntryAmount(0);
                setQuickAmountsMode('set');
                setCurrency(Currency.Usd, activeGameMode);
            }

            const { refferer } = useBetslipStore.getState();
            if (refferer?.linkId || refferer?.entryId) {
                markLinkConversion({ referrer: refferer, amount: newEntry.amount, entryId: newEntry.id });
            }

            const maxEntryAmountExceeded = newEntry.amount !== newEntry.initialAmount;
            if (newEntry.amount && newEntry.initialAmount && maxEntryAmountExceeded) {
                const placedEntryAmount = newEntry.amount;
                const creditedBack = newEntry.initialAmount - placedEntryAmount;
                BetrAnalytics.trackEvent(AnalyticsEvent.P2P_MAX_ENTRY_EXCEEDED);
                showInfoSheet({
                    title: t('leaderboard_groups_maxed_out'),
                    titleVariant: 'titleLarge',
                    description: (
                        <P2pMaxEntryAmountExceeded placedEntryAmount={placedEntryAmount} creditedBack={creditedBack} />
                    ),
                    buttonLabel: t('gotIt'),
                });
            }

            loadUserWallet();
            queryClient.invalidateQueries({ queryKey: userLimitsKeys.limits() });
            queryClient.invalidateQueries({ queryKey: userPromosKeys.all });
        },
        [
            isMysteryMode,
            loadUserWallet,
            queryClient,
            setSelectedPromo,
            resetAutoPromoApplied,
            setEntryAmount,
            setQuickAmountsMode,
            setCurrency,
            activeGameMode,
            showInfoSheet,
            t,
        ]
    );

    const handleError = useCallback(
        (e: unknown, onPress?: () => void) => {
            BetrAnalytics.trackEvent(AnalyticsEvent.PICKS_ENTRY_ERROR, { activeGameMode: activeGameMode });
            logger.warn('[SubmitEntry]', e);

            const { refferer, actions } = useBetslipStore.getState();
            const { raceToPlaceEntryId } = refferer || {};

            const isRaceToPlace = !!raceToPlaceEntryId;
            if (isRaceToPlace) actions.clearRefferer();
            if (e instanceof CreateEntryError && hasInvalidProjections(e) && !isRaceToPlace) {
                onInvalidProjectionsFound?.();
                return;
            }

            const error = getErrorInfo(e);
            showInfoSheet({
                title: error.title,
                description: error.subtitle ?? '',
                buttonLabel: error.primaryButton,
                containerStyle: { paddingBottom: 0 },
                icon: (
                    <Box paddingTop={'s16'}>
                        <ErrorIcon />
                    </Box>
                ),
                handlePress:
                    error.needsLocationAccuracy || onPress
                        ? () => {
                              if (error.needsLocationAccuracy) openLocationSettings();
                              onPress?.();
                          }
                        : undefined,
            });
        },
        [activeGameMode, onInvalidProjectionsFound, showInfoSheet]
    );

    return { handleSuccess, handleError };
};
